import { z } from 'zod'

import { addDays, lastMonth, presetRange } from '@/lib/dates'
import { pruned } from '@/lib/filters'

export const REVIEW_PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_REVIEW_PAGE_SIZE = 100

/** VentaHub's quick periods: the last 7 or 30 days, or last month, each against the one before. */
export const REVIEW_PRESETS = ['7d', '30d', 'month'] as const
export type ReviewPreset = (typeof REVIEW_PRESETS)[number]

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const ids = z.array(z.number().int().positive()).max(50).optional().catch(undefined)

/**
 * Review Data's URL: the reports' scope (warehouses, the marketplace, its
 * stores, brands); the two periods; Focused only and Hide inactive;
 * whether the report has been generated; the search, the sort and the page;
 * whether the summary is open, and the currency it reads. Every field optional; defaults apply at read time.
 */
export const reviewDataSearchSchema = z.object({
  warehouseIds: ids.describe('Warehouses to review, by id; at least one is needed to generate.'),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The marketplace to read, by id; absent means every marketplace.'),
  storeIds: ids.describe('Stores to include, by id; empty means every store.'),
  brandIds: ids.describe('Brands to include, by id; empty means every brand.'),
  previousFrom: day
    .optional()
    .catch(undefined)
    .describe(
      'First day of the previous period; absent, the period of equal length just before the current one.',
    ),
  previousTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the previous period; absent, the day before the current period starts.'),
  currentFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the current period, YYYY-MM-DD; the last 30 days when absent.'),
  currentTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the current period, YYYY-MM-DD, inclusive; today when absent.'),
  focused: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 is Focused only: just the focused SKUs.'),
  // Hide inactive: leave out the SKUs that neither sold nor hold stock.
  active: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 is Hide inactive: SKUs that neither sold nor hold stock are left out.'),
  // Set by Generate: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe(
      '1 once Generate is pressed; nothing loads without it or without a warehouse. Filter and period changes clear it.',
    ),
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Search text over the rows (SKU code, name, brand or store).'),
  sort: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column id the rows are sorted by; absent is the report’s own order.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction; desc when a sort is set.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the table, from 1; default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page; default 100.'),
  // The summary, opened: absent is folded, as VentaHub opened the page.
  summary: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 opens the Summary panel; absent it is folded.'),
  // The currency the summary's amounts are read in; absent, the first.
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional()
    .catch(undefined)
    .describe(
      'The currency the summary reads its amounts in; absent, the first one in the result.',
    ),
})

export type ReviewDataSearch = z.infer<typeof reviewDataSearchSchema>

export const EMPTY_REVIEW_DATA_SEARCH: ReviewDataSearch = reviewDataSearchSchema.parse({})

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_REVIEW_DATA: Partial<ReviewDataSearch> = Object.fromEntries(
  Object.keys(reviewDataSearchSchema.shape).map((key) => [key, undefined]),
)

type Range = { from: string; to: string }

/** The period of the same length that ends the day before `range` starts. */
export function periodBefore(range: Range): Range {
  const days = Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86_400_000) + 1
  const to = addDays(range.from, -1)
  return { from: addDays(to, -(days - 1)), to }
}

/** A quick period and the one before it. */
export function presetPeriods(preset: ReviewPreset): { previous: Range; current: Range } {
  if (preset === 'month') {
    const current = lastMonth(new Date())
    const end = addDays(current.from, -1)
    return { current, previous: { from: `${end.slice(0, 8)}01`, to: end } }
  }
  const current = presetRange(preset === '7d' ? 'last7' : 'last30')
  return { current, previous: periodBefore(current) }
}

/** The periods the URL asks for: the last 30 days against the 30 before, unless set. */
export function periodsOf(search: ReviewDataSearch): { previous: Range; current: Range } {
  const fallback = presetPeriods('30d')
  const current = {
    from: search.currentFrom ?? fallback.current.from,
    to: search.currentTo ?? fallback.current.to,
  }
  const previous =
    search.previousFrom && search.previousTo
      ? { from: search.previousFrom, to: search.previousTo }
      : periodBefore(current)
  return { previous, current }
}

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutReviewDataDefaults(search: ReviewDataSearch): ReviewDataSearch {
  return pruned({
    ...search,
    limit: search.limit === DEFAULT_REVIEW_PAGE_SIZE ? undefined : search.limit,
  })
}
